/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ComponentProps, JSX } from 'preact';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';
import { merge } from '@oracle/oraclejet-internal-utilities/stringUtils';

import { useMessageSeverity } from '#hooks/PRIVATE_useMessageSeverity';
import type { InlineUserAssistance } from '#UNSAFE_UserAssistance';

type UseCheckboxRadioFieldProps = {
  ariaDescribedBy?: string;
  baseId: string;
  hasValue?: boolean;
  hasTopUserAssistance?: boolean;
  hasBottomUserAssistance?: boolean;
  isFocused?: boolean; // TODO: check if this is needed
  isReadonly?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  label: string;
  labelEdge?: 'none' | 'top' | 'start' | 'inside';
  messages?: ComponentProps<typeof InlineUserAssistance>['messages'];
  parentComponentVariant: 'radioSet' | 'checkboxSet';
};

type GroupProps = {
  groupProps: {
    'aria-describedby'?: UnpackSignals<JSX.AriaAttributes>['aria-describedby'];
    'aria-labelledby'?: UnpackSignals<JSX.AriaAttributes>['aria-labelledby'];
    'aria-label'?: UnpackSignals<JSX.AriaAttributes>['aria-label'];
    'aria-required'?: UnpackSignals<JSX.AriaAttributes>['aria-required'];
    'aria-invalid'?: UnpackSignals<JSX.AriaAttributes>['aria-invalid'];
    'aria-readonly'?: UnpackSignals<JSX.AriaAttributes>['aria-readonly'];
    'aria-disabled'?: UnpackSignals<JSX.AriaAttributes>['aria-disabled'];
    id: UnpackSignals<JSX.HTMLAttributes>['id'];
  };
  labelProps: {
    forId: string;
    id: string;
    isRequiredShown?: boolean;
  };
  topUserAssistanceProps?: {
    id: string;
    variant: 'reflow';
  };
  bottomUserAssistanceProps?: {
    id: string;
    isRequiredShown?: boolean;
  };
  readonlyId?: string;
};

export function useCheckboxRadioField({
  ariaDescribedBy,
  baseId,
  hasValue,
  hasTopUserAssistance,
  hasBottomUserAssistance,
  isReadonly,
  isRequired = false,
  isDisabled,
  label,
  labelEdge,
  messages,
  parentComponentVariant
}: UseCheckboxRadioFieldProps): GroupProps {
  const optionsGroupId = `${baseId}-group`;
  const labelId = `${baseId}-label`;
  const topUaId = hasTopUserAssistance ? `${baseId}-top-ua` : undefined;
  const bottomUaId = hasBottomUserAssistance ? `${baseId}-bottom-ua` : undefined;
  const readonlyId = isReadonly ? `${baseId}-readonly` : undefined;
  const ariaLabel = label && label.length > 0 && labelEdge === 'none' ? label : undefined;
  const ariaLabelledBy = ariaLabel ? undefined : labelId;
  const isRadioSet = parentComponentVariant === 'radioSet';
  const highestMessageSeverity = useMessageSeverity(messages);
  const hasErrorMessage = highestMessageSeverity === 'error';

  return {
    groupProps: {
      'aria-describedby': merge([topUaId, bottomUaId, readonlyId, ariaDescribedBy]),
      ...(isRadioSet && { 'aria-required': isRequired }),
      ...(ariaLabelledBy && { 'aria-labelledby': ariaLabelledBy }),
      ...(ariaLabel && { 'aria-label': ariaLabel }),
      ...(isReadonly && isRadioSet && { 'aria-readonly': true }),
      ...(hasErrorMessage && { 'aria-invalid': true }),
      ...(isDisabled && { 'aria-disabled': true }),
      id: optionsGroupId
    },
    labelProps: {
      forId: optionsGroupId,
      id: labelId
    },
    // TOP UA is assistive text and help link/text
    ...(hasTopUserAssistance && {
      topUserAssistanceProps: {
        id: topUaId as string,
        // Use reflow variant so that top UA doesn't have min height
        variant: 'reflow'
      }
    }),
    // Bottom UA is required indicator or messages
    ...(hasBottomUserAssistance && {
      bottomUserAssistanceProps: {
        id: bottomUaId as string,
        isRequiredShown: !isReadonly && !!isRequired && !hasValue
      }
    }),
    ...(isReadonly && {
      readonlyId
    })
  };
}
